import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Divider } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import Drawer from 'components/Drawer';
import useCreatePackage from 'hooks/package/useCreatePackage';
import usePackage from 'hooks/package/usePackage';
import useUpdatePackage from 'hooks/package/useUpdatePackage';
import notificationHandler from 'utils/notifications';

import BillingPanel from './components/BillingPanel';
import CreationWarningModal from './components/CreationWarningModal';
import Footer from './components/Footer';
import Header from './components/Header';
import { CreditBasedPackageForm, TimeBasedPackageForm } from './components/PackageForm';
import PackageTypeSelection from './components/PackageTypeSelection';
import usePackageDrawerStyles from './styles';
import PackageDrawerState, { PackageType } from './types';

const formId = 'package-form';

interface IPackageDrawerProps {
  isOpen: boolean;
  closeDrawer: () => void;
  state: PackageDrawerState;
  setState: React.Dispatch<React.SetStateAction<PackageDrawerState>>;
  packageId: string;
  setPackageId: React.Dispatch<React.SetStateAction<string>>;
  permission?: boolean;
  packageType?: PackageType;
  setPackageType?: React.Dispatch<React.SetStateAction<PackageType | undefined>>;
}

const PackageDrawer = ({
  isOpen,
  closeDrawer,
  state,
  setState,
  packageId,
  setPackageId,
  permission = true,
  packageType,
  setPackageType = () => {},
}: IPackageDrawerProps): JSX.Element => {
  const { classes } = usePackageDrawerStyles();
  const packageHook = usePackage(packageId);
  const createPackageMutation = useCreatePackage();
  const updatePackageMutation = useUpdatePackage();
  const [hasBillingPlans, setHasBillingPlans] = useState<boolean>(false);
  const [isCreateWarningModalOpen, createWarningModalHandlers] = useDisclosure(false);

  const handleCreationWarningModalConfirm = (): void => {
    const { successNotification } = notificationHandler();
    createWarningModalHandlers.close();
    closeDrawer();
    successNotification('Package creation successful');
  };

  const handlePackageTypeSelect = useCallback(
    (selectedType: PackageType): void => {
      setPackageType(selectedType);
      setState('create-package');
    },
    [setPackageType, setState],
  );

  const handleStepChange = (step: PackageDrawerState): void => {
    setState(step);
  };

  useEffect(() => {
    if (packageHook.data?.data.billingPlans && packageHook.data?.data.billingPlans.length > 0) {
      setHasBillingPlans(true);
    } else {
      setHasBillingPlans(false);
    }
  }, [packageHook.data]);

  const FormStepContent: JSX.Element = useMemo(() => {
    const FormComponent = packageType === 'CREDIT_BASED' ? CreditBasedPackageForm : TimeBasedPackageForm;

    return (
      <div className={classes.content}>
        {state === 'select-type' && (
          <PackageTypeSelection selectedType={packageType || null} onSelect={handlePackageTypeSelect} />
        )}

        {(state === 'view-package' ||
          state === 'create-package' ||
          state === 'create-billing' ||
          state === 'edit-package' ||
          state === 'edit-billing') && (
          <FormComponent
            state={state}
            setState={setState}
            setPackageId={setPackageId}
            formId={formId}
            packageHook={packageHook}
            createPackageMutation={createPackageMutation}
            updatePackageMutation={updatePackageMutation}
            permission={permission}
            packageType={packageType}
          />
        )}

        <Divider orientation='vertical' />

        <BillingPanel
          state={state}
          packageHook={packageHook}
          packageId={packageId}
          permission={permission}
        />
      </div>
    );
  }, [
    classes.content,
    createPackageMutation,
    handlePackageTypeSelect,
    packageHook,
    packageId,
    packageType,
    permission,
    setPackageId,
    setState,
    state,
    updatePackageMutation,
  ]);

  return (
    <Drawer
      opened={isOpen}
      onClose={closeDrawer}
      closeOnClickOutside={false}
      size={1400}
      title={
        <Header packageHook={packageHook} state={state} permission={permission} packageType={packageType} />
      }
      footer={
        <Footer
          state={state}
          setState={setState}
          closeDrawer={closeDrawer}
          formId={formId}
          packageId={packageId}
          createPackageMutation={createPackageMutation}
          updatePackageMutation={updatePackageMutation}
          permission={permission}
          hasBillingPlans={hasBillingPlans}
          isArchived={packageHook.data?.data.isArchived}
          createWarningModalHandlers={createWarningModalHandlers}
          onStepChange={handleStepChange}
          packageType={packageType}
        />
      }
      padding='0'
    >
      {FormStepContent}
      <CreationWarningModal
        opened={isCreateWarningModalOpen}
        handleConfirm={handleCreationWarningModalConfirm}
        handleCancel={createWarningModalHandlers.close}
      />
    </Drawer>
  );
};
export default PackageDrawer;
